/**
 * Lightspeed connect — the location picker.
 *
 * Lands here from the OAuth callback carrying only an opaque `pending` id; the
 * tokens never touch the browser. One Lightspeed authorization can cover many
 * business locations and each becomes its own source (one grant, one sync, one
 * billing line), so the choice is explicit rather than "connect everything".
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertCircle, ArrowRight, Building2, Check } from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import ConnectTargetOrgSelect from '../../components/integrations/ConnectTargetOrgSelect';
import { useConnectTargetOrg } from '../../hooks/useConnectTargetOrg';
import { lightspeedApi, type LightspeedLocation } from '../../services/lightspeed-api';

const LightspeedConnectPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { orgs, targetOrgId, setTargetOrgId, hasChoice } = useConnectTargetOrg();

  const pendingId = searchParams.get('pending');
  const oauthError = searchParams.get('error');
  const oauthErrorDescription = searchParams.get('error_description');

  const [locations, setLocations] = useState<LightspeedLocation[] | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!pendingId || oauthError || !targetOrgId) return;

    let cancelled = false;
    lightspeedApi
      .getPendingLocations(pendingId, targetOrgId)
      .then(({ locations: found }) => {
        if (cancelled) return;
        setLocations(found);
        // The overwhelmingly common case is a single restaurant — preselect it
        // so the flow is one click, without hiding that a choice was made.
        const connectable = found.filter((l) => !l.alreadyConnected);
        if (connectable.length === 1) {
          setSelected(new Set([connectable[0].businessLocationId]));
        }
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        const message =
          (error as { response?: { data?: { error?: string } } })?.response?.data?.error ??
          'Could not load your Lightspeed locations.';
        setLoadError(message);
      });

    return () => {
      cancelled = true;
    };
  }, [pendingId, oauthError, targetOrgId]);

  const connectable = useMemo(
    () => (locations ?? []).filter((l) => !l.alreadyConnected),
    [locations],
  );

  const toggle = (businessLocationId: number): void => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(businessLocationId)) next.delete(businessLocationId);
      else next.add(businessLocationId);
      return next;
    });
  };

  const handleConnect = async (): Promise<void> => {
    if (!pendingId || !targetOrgId || selected.size === 0) return;

    setSubmitting(true);
    try {
      const { dataSourceIds } = await lightspeedApi.complete(
        pendingId,
        [...selected],
        targetOrgId,
      );
      toast.success(
        dataSourceIds.length > 1
          ? `Connected ${dataSourceIds.length} Lightspeed locations`
          : 'Lightspeed location connected',
      );
      navigate('/sources/integrations');
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        'Failed to connect the selected locations.';
      toast.error(message);
      setSubmitting(false);
    }
  };

  const failure = oauthError
    ? oauthErrorDescription || 'Lightspeed did not complete the authorization.'
    : !pendingId
      ? 'This page needs an authorization to finish. Start the connection from Integrations.'
      : loadError;

  if (failure) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-8 text-center">
          <div className="mx-auto w-16 h-16 bg-destructive/15 rounded-full flex items-center justify-center mb-4">
            <AlertCircle className="w-10 h-10 text-destructive" />
          </div>
          <h2 className="text-2xl font-bold text-foreground mb-2">Connection Failed</h2>
          <p className="text-muted-foreground mb-6">{failure}</p>
          <Button variant="primary" className="w-full" onClick={() => navigate('/sources/integrations')}>
            Back to Integrations
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="max-w-lg w-full p-8">
        <h2 className="text-2xl font-bold text-foreground mb-1">Choose your locations</h2>
        <p className="text-muted-foreground mb-6">
          Each location you connect becomes its own data source, so you can share one restaurant
          without sharing the rest.
        </p>

        {hasChoice && (
          <div className="mb-5">
            <ConnectTargetOrgSelect
              orgs={orgs}
              value={targetOrgId}
              onChange={setTargetOrgId}
            />
          </div>
        )}

        {!targetOrgId ? (
          <p className="text-sm text-muted-foreground">
            Choose which business these locations belong to first.
          </p>
        ) : !locations ? (
          <div className="py-10 text-center">
            <LoadingSpinner size="lg" className="mx-auto mb-4" />
            <p className="text-muted-foreground">Reading your Lightspeed locations…</p>
          </div>
        ) : (
          <>
            <div className="space-y-2 max-h-[45vh] overflow-y-auto">
              {locations.map((location) => {
                const isSelected = selected.has(location.businessLocationId);
                const disabled = location.alreadyConnected;

                return (
                  <button
                    key={location.businessLocationId}
                    type="button"
                    disabled={disabled}
                    onClick={() => toggle(location.businessLocationId)}
                    className={clsx(
                      'w-full flex items-center gap-3 rounded-lg border p-3 text-left transition-colors',
                      disabled
                        ? 'border-border bg-muted/40 cursor-not-allowed opacity-60'
                        : isSelected
                          ? 'border-primary bg-accent'
                          : 'border-border bg-card hover:bg-accent',
                    )}
                  >
                    <span
                      className={clsx(
                        'w-5 h-5 rounded border flex items-center justify-center flex-none',
                        isSelected ? 'bg-primary border-primary' : 'border-border',
                      )}
                    >
                      {isSelected && <Check className="w-3.5 h-3.5 text-primary-foreground" />}
                    </span>
                    <Building2 className="w-4 h-4 text-muted-foreground flex-none" />
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium text-foreground truncate">
                        {location.businessLocationName || `Location ${location.businessLocationId}`}
                      </span>
                      <span className="block text-xs text-muted-foreground truncate">
                        {[location.businessName, location.country, location.currencyCode]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                    </span>
                    {disabled && (
                      <span className="text-xs text-muted-foreground flex-none">Connected</span>
                    )}
                  </button>
                );
              })}
            </div>

            {connectable.length === 0 && (
              <p className="mt-4 text-sm text-muted-foreground">
                Every location this Lightspeed account can reach is already connected.
              </p>
            )}

            <Button
              variant="primary"
              className="w-full mt-6"
              disabled={selected.size === 0 || submitting}
              isLoading={submitting}
              rightIcon={!submitting ? <ArrowRight className="w-4 h-4" /> : undefined}
              onClick={handleConnect}
            >
              {selected.size > 1 ? `Connect ${selected.size} locations` : 'Connect location'}
            </Button>
          </>
        )}
      </Card>
    </div>
  );
};

export default LightspeedConnectPage;
